import {
  createAsyncThunk,
  createDraftSafeSelector,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import _ from 'lodash';
import * as api from '../api/api';
import { selectMount } from '../dff/dff-slice';
import { typeSortMap } from '../dff/dff-state';
import { AppState, Range } from '../store/app-state';

function getRanges(n: number, limit: number) {
  const rangesNew: Range[] = [];
  if (!n || n < 1) {
    return rangesNew;
  }
  for (let i = 1; i <= limit; i += n) {
    if (i + n - 1 <= limit) {
      rangesNew.push({
        fromPage: i,
        toPage: i + n - 1,
        name: `Range ${(rangesNew.length + 1).toString()}`,
      });
    } else {
      rangesNew.push({
        fromPage: i,
        toPage: limit,
        name: `Range ${(rangesNew.length + 1).toString()}`,
      });
    }
  }
  return rangesNew;
}

export const fetchPdfs = createAsyncThunk(
  'pdfs/fetchPdfs',
  async (obj: { mountId: string; query: string }, { getState, dispatch }) => {
    let url: string;
    if (obj.mountId === null) {
      const state = getState();
      const selectedMount = (state as AppState).dff.mount;
      if (selectedMount.id === 'recent') {
        const m = selectMounts(state as AppState);
        url = `/api/v2/search?query=${obj.query}&limit=100&sortField=mtime&sortDir=desc&mountId=${m[0].id}&path=&fileType=pdf`;
      } else {
        url = `/api/v2/search?query=${obj.query}&limit=100&sortField=mtime&sortDir=desc&mountId=${selectedMount.id}&path=&fileType=pdf`;
      }
    } else {
      url =
        obj.mountId === 'recent'
          ? `/api/v2/search?query=${obj.query}&limit=100&sortField=mtime&sortDir=desc&mountId=&path=&fileType=pdf`
          : `/api/v2/search?query=${obj.query}&limit=100&sortField=mtime&sortDir=desc&mountId=${obj.mountId}&path=&fileType=pdf`;
    }

    return fetch(url)
      .then((res) => {
        return res.json();
      })
      .then((data) => {
        const mappedHits = data.hits.map((hit: api.SearchHit) => {
          return {
            file: hit,
            mountId: hit.mountId,
            path: hit.path,
            selectedMerge: false,
            rotateDegrees: 0,
            order: -1,
          };
        });
        return mappedHits;
      });
  },
);

export const fetchMounts = createAsyncThunk(
  'pdfs/fetchMounts',
  async (obj: { loadRecent?: boolean }, { getState, dispatch }) => {
    await dispatch(resetPdfs());

    const results = await fetch('/api/v2/mounts');
    const data = await results.json();
    let mountsTemp = data.mounts;
    const state = getState();
    const selectedMount = (state as AppState).dff.mount;
    mountsTemp = mountsTemp.filter(
      (mount: api.Mount) => mount.online && mount.origin === 'hosted',
    );
    mountsTemp = _.sortBy(mountsTemp, (mount_1: api.Mount) => {
      return [typeSortMap[mount_1.type], -mount_1.isPrimary, mount_1.name];
    });
    if (obj.loadRecent) {
      mountsTemp.unshift({
        id: 'recent',
        name: 'Most recent',
      });
    }
    if (!selectedMount) {
      dispatch(selectMount(mountsTemp[0]));
    }
    return mountsTemp;
  },
);

export const pdfSlice = createSlice({
  name: 'pdf',
  initialState: {
    mounts: [],
    pdfs: [],
    selectedPdf: null,
    pages: {},
    filteredPdfs: [],
    isExtractPagesVisible: true,
    isSelectPagesVisible: false,
    isSplitRangeVisible: false,
    isFixedRangesVisible: false,
    isCustomRangeVisible: false,
    mergeExtractedPages: false,
    splitRanges: [],
    fixedRange: 1,
    fileName: undefined,
    sortDescending: true,
    mergeFileName: 'merged',
    searchText: '',
    isSplitting: false,
    finalFiles: [],
    errorMessage: null,
  },
  reducers: {
    toggleSelectPagesButton: (state) => {
      let newPages = {};
      if (!state.isSelectPagesVisible) {
        Object.keys(state.pages).forEach((k) => {
          newPages[k] = {
            ...state.pages[k],
            selected: false,
          };
        });
      } else {
        Object.keys(state.pages).forEach((k) => {
          newPages[k] = {
            ...state.pages[k],
            selected: true,
          };
        });
      }
      return {
        ...state,
        isSelectPagesVisible: !state.isSelectPagesVisible,
        pages: newPages,
      };
    },
    showSelectPages: (state) => {
      return {
        ...state,
        isSelectPagesVisible: true,
      };
    },
    toggleMergeExtractedPages: (state) => {
      return {
        ...state,
        mergeExtractedPages: !state.mergeExtractedPages,
      };
    },
    toggleFixedRangesVisible: (state) => {
      const rangesNew = !state.isFixedRangesVisible
        ? getRanges(1, state.selectedPdf.pages)
        : getRanges(state.selectedPdf.pages, state.selectedPdf.pages);
      return {
        ...state,
        splitRanges: rangesNew,
        isFixedRangesVisible: !state.isFixedRangesVisible,
        isCustomRangeVisible: !state.isCustomRangeVisible,
      };
    },
    showSplitPanel: (state) => {
      let rangesNew: Range[] = state.splitRanges;
      if (!state.splitRanges.length) {
        rangesNew = getRanges(state.selectedPdf.pages, state.selectedPdf.pages);
      }
      return {
        ...state,
        splitRanges: rangesNew,
        isExtractPagesVisible: false,
        isSplitRangeVisible: true,
        isSplitting: false,
      };
    },
    showExtractPanel: (state) => {
      return {
        ...state,
        isExtractPagesVisible: true,
        isSplitRangeVisible: false,
        isSplitting: false,
      };
    },
    addRange: (
      state,
      { payload }: PayloadAction<{ fromPage: number; toPage: number }>,
    ) => {
      state.splitRanges.push({
        fromPage: payload.fromPage,
        toPage: payload.toPage,
        name: `Range ${state.splitRanges.length + 1}`,
      });
    },
    removeRange: (state, { payload }) => {
      state.splitRanges.splice(payload, 1);
      state.splitRanges.forEach((range, i) => {
        range.name = `Range ${i + 1}`;
      });
    },
    editRange: (state, { payload }) => {
      state.splitRanges[payload.index] = {
        fromPage: payload.fromPage,
        toPage: payload.toPage,
        name: payload.name,
      };
    },
    setFixedRange: (state, { payload }) => {
      const rangesNew = getRanges(payload, state.selectedPdf.pages);
      return {
        ...state,
        splitRanges: rangesNew,
        fixedRange: payload,
      };
    },
    setSelectedPdf: (state, { payload }) => {
      return {
        ...state,
        selectedPdf: payload,
      };
    },
    setFileName: (state, { payload }) => {
      return {
        ...state,
        fileName: payload,
      };
    },
    addPage: (state, { payload }) => {
      const { page, thumbnail } = payload;
      state.pages[page] = {
        thumbnail: thumbnail,
        selected: state.pages[page] ? state.pages[page].selected : true,
      };
    },
    setPages: (state, { payload }) => {
      return {
        ...state,
        pages: payload,
      };
    },
    togglePageSelected: (state, { payload }) => {
      state.pages[payload].selected = !state.pages[payload].selected;
    },
    editPagesSelected: (state, { payload }) => {
      for (let i = 1; i <= state.selectedPdf.pages; i++) {
        if (payload.find((e) => e === i)) {
          state.pages[i].selected = true;
        } else {
          state.pages[i].selected = false;
        }
      }
    },
    toggleMergeSelect: (state, { payload }) => {
      const foundFileIndex = state.pdfs.findIndex((pdf) => {
        return pdf.path === payload.file.path;
      });
      if (foundFileIndex !== -1) {
        const isCurrentlySelected = state.pdfs[foundFileIndex].selectedMerge;

        if (isCurrentlySelected) {
          const currentOrder = state.pdfs[foundFileIndex].order;
          state.pdfs[foundFileIndex].selectedMerge = false;
          state.pdfs[foundFileIndex].order = -1;

          state.pdfs.forEach((pdf) => {
            if (pdf.selectedMerge && pdf.order > currentOrder) {
              pdf.order -= 1;
            }
          });
        } else {
          const maxOrder = state.pdfs.reduce((max, pdf) => {
            return pdf.selectedMerge && pdf.order > max ? pdf.order : max;
          }, -1);

          state.pdfs[foundFileIndex].selectedMerge = true;
          state.pdfs[foundFileIndex].order = maxOrder + 1;
        }
      }
    },
    rotatePDF: (state, { payload }) => {
      const foundFileIndex = state.pdfs.findIndex((pdf) => {
        return pdf.path === payload.file.path;
      });
      if (foundFileIndex !== -1) {
        if (state.pdfs[foundFileIndex].rotateDegrees === -270) {
          state.pdfs[foundFileIndex].rotateDegrees = 0;
        } else {
          state.pdfs[foundFileIndex].rotateDegrees -= 90;
        }
      }
    },
    toggleSortPDFs: (state) => {
      return {
        ...state,
        sortDescending: !state.sortDescending,
      };
    },
    setMergeFileName: (state, { payload }) => {
      return {
        ...state,
        mergeFileName: payload,
      };
    },
    setSearchText: (state, { payload }) => {
      return {
        ...state,
        searchText: payload,
      };
    },
    setSplitting: (state, { payload }) => {
      return {
        ...state,
        isSplitting: payload,
      };
    },
    setFinalFiles: (state, { payload }) => {
      return {
        ...state,
        finalFiles: payload,
      };
    },
    setFileOrder: (state, { payload }) => {
      const foundFileIndex = state.pdfs.findIndex((pdf) => {
        return pdf.path === payload.file.path;
      });
      if (foundFileIndex !== -1) {
        state.pdfs[foundFileIndex].order = payload.order;
      }
    },
    resetPdfs: (state) => {
      return {
        ...state,
        pdfs: [],
      };
    },
    resetState: (state) => {
      return {
        ...state,
        // pdfs: [],
        // selectedPdf: null,
        pages: {},
        filteredPdfs: [],
        isExtractPagesVisible: true,
        isSelectPagesVisible: false,
        isSplitRangeVisible: false,
        isFixedRangesVisible: false,
        isCustomRangeVisible: false,
        mergeExtractedPages: false,
        splitRanges: [],
        fixedRange: 1,
        fileName: undefined,
        mergeFileName: 'merged',
        searchText: '',
        isSplitting: false,
        finalFiles: [],
        errorMessage: null,
      };
    },
    setErrorMessage: (state, { payload }) => {
      state.errorMessage = payload;
    },
    clearErrorMessage: (state) => {
      state.errorMessage = null;
    },
  },
  extraReducers: {
    [fetchPdfs.fulfilled as any]: (state, action) => {
      // Preserve existing order and selection state when fetching PDFs
      const existingPdfsMap = new Map(
        state.pdfs.map(pdf => [pdf.path, pdf])
      );

      state.pdfs = action.payload.map((newPdf: any) => {
        const existing = existingPdfsMap.get(newPdf.path);
        if (existing) {
          return {
            ...newPdf,
            order: existing.order,
            selectedMerge: existing.selectedMerge,
            rotateDegrees: existing.rotateDegrees,
          };
        }
        return newPdf;
      });
    },
    [fetchMounts.fulfilled as any]: (state, action) => {
      state.mounts = action.payload;
    },
  },
});

export const {
  toggleFixedRangesVisible,
  toggleSelectPagesButton,
  toggleMergeExtractedPages,
  showSplitPanel,
  showExtractPanel,
  addRange,
  removeRange,
  editRange,
  setFixedRange,
  setSelectedPdf,
  addPage,
  setPages,
  togglePageSelected,
  showSelectPages,
  editPagesSelected,
  setFileName,
  toggleMergeSelect,
  rotatePDF,
  toggleSortPDFs,
  resetState,
  setMergeFileName,
  setSearchText,
  setSplitting,
  setFinalFiles,
  setFileOrder,
  resetPdfs,
  setErrorMessage,
  clearErrorMessage,
} = pdfSlice.actions;

export const pdfState = (state: AppState) => state.pdf;
export const selectPdfs = createDraftSafeSelector(
  pdfState,
  (state) => state.pdfs,
);
export const selectMounts = createDraftSafeSelector(
  pdfState,
  (state) => state.mounts,
);
export const selectPagesInputVisible = createSelector(
  pdfState,
  (state) => state.isSelectPagesVisible,
);
export const extractButtonText = createDraftSafeSelector(pdfState, (state) =>
  state.isSelectPagesVisible ? 'Extract all pages' : 'Select pages',
);
export const splitButtonText = createDraftSafeSelector(pdfState, (state) =>
  state.isFixedRangesVisible ? 'Custom ranges' : 'Fixed ranges',
);
export const selectMergeExtractedPages = createDraftSafeSelector(
  pdfState,
  (state) => state.mergeExtractedPages,
);
export const selectExtractPanelVisible = createDraftSafeSelector(
  pdfState,
  (state) => state.isExtractPagesVisible,
);
export const selectSplitPanelVisible = createDraftSafeSelector(
  pdfState,
  (state) => state.isSplitRangeVisible,
);
export const selectFixedRangesVisible = createDraftSafeSelector(
  pdfState,
  (state) => state.isFixedRangesVisible,
);
export const selectCustomRangesVisible = createDraftSafeSelector(
  pdfState,
  (state) => state.isCustomRangeVisible,
);
export const selectSplitRanges = createDraftSafeSelector(
  pdfState,
  (state) => state.splitRanges,
);
export const selectFixedRange = createDraftSafeSelector(
  pdfState,
  (state) => state.fixedRange,
);

export const selectPages = createDraftSafeSelector(
  pdfState,
  (state) => state.pages,
);

export const selectSelectedPages = createDraftSafeSelector(pdfState, (state) =>
  Object.entries(state.pages).map((a, b) => ({
    id: b,
    selected: a[1].selected,
  })),
);

export const selectSelectedPDF = createDraftSafeSelector(
  pdfState,
  (state) => state.selectedPdf,
);

export const selectFileName = createDraftSafeSelector(
  pdfState,
  (state) => state.fileName,
);

export const selectFilesToMerge = createDraftSafeSelector(pdfState, (state) =>
  state.pdfs.filter((pdf) => pdf.selectedMerge),
);

export const selectSortDescending = createDraftSafeSelector(
  pdfState,
  (state) => state.sortDescending,
);

export const selectMergeFileName = createDraftSafeSelector(
  pdfState,
  (state) => state.mergeFileName,
);
export const selectSearchText = createDraftSafeSelector(
  pdfState,
  (state) => state.searchText,
);
export const selectIsSplitting = createDraftSafeSelector(
  pdfState,
  (state) => state.isSplitting,
);
export const selectFinalFiles = createDraftSafeSelector(
  pdfState,
  (state) => state.finalFiles,
);
export const selectErrorMessage = createDraftSafeSelector(
  pdfState,
  (state) => state.errorMessage,
);
export const selectFinalPDFs = createDraftSafeSelector(
  selectSelectedPages,
  selectMergeExtractedPages,
  selectFixedRangesVisible,
  selectSplitPanelVisible,
  selectSplitRanges,
  (pages, merge, isFixedRangesVisible, isSplitPanelVisible, splitRanges) => {
    if (isFixedRangesVisible && isSplitPanelVisible) {
      return splitRanges.length;
    }
    if (merge) {
      return 1;
    }
    return pages.filter((p) => p.selected).length;
  },
);

export default pdfSlice.reducer;
